import React, { useState, useEffect, createContext } from "react";

import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  updateProfile,
  onIdTokenChanged,
} from "firebase/auth";

import firebaseAuth from "config/firebaseConfig";
import { axiosInstance, setAuthToken } from "api/apiClient";


export const AuthContext = createContext(null);

export const ContextProvider = (props) => {
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [userInfos, setUserInfos] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLeadId, setSelectedLeadId] = useState(null);

  useEffect(() => {
    const unsubscribeAuthState = onAuthStateChanged(firebaseAuth, async (user) => { // Added async
      setIsLoading(true);
      if (user) {
        setIsSignedIn(true);
        setUser(user);
        try {
          const token = localStorage.getItem("jwtToken");
          if (token) {
            setAuthToken(token);
            // Waits for getUserInfos to finish before proceeding
            const userInfo = await getUserInfos(user.uid); 
            setUserInfos(userInfo);  // Assuming this setUserInfos refers to a state updater and not the same name function.
            console.log("getUserInfos : ", userInfo);
          }
        } catch (error) {
          console.error("Error fetching user info:", error);
        }
      } else {
        handleSignOutState();
      }
      setIsLoading(false);
    });

    // Set up token refresh listener
    const unsubscribeIdToken = onIdTokenChanged(firebaseAuth, async (user) => {
      if (user) {
        const token = await user.getIdToken();
        setAuthToken(token);
      } else {
        handleSignOutState();
      }
    });

    const handleSignOutState = () => {
      setIsSignedIn(false);
      setUser(null);
      setUserInfos(null);
      setAuthToken(null);
    };

    // Cleanup listeners on unmount
    return () => {
      unsubscribeAuthState();
      unsubscribeIdToken();
    };
  }, []);

  const signIn = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        firebaseAuth,
        email,
        password
      );
      const idToken = await userCredential.user.getIdToken();
      setAuthToken(idToken);  // Set token for future requests
      console.info("User signed in successfully : ", userCredential.user);
      const userInfo = await getUserInfos(userCredential.user.uid); 
      setUserInfos(userInfo);
      return;
    } catch (err) {
      console.error(err.message);
      return err.message;
    }
  };

  const getUserInfos = async (uid) => {
    try {
      const response = await axiosInstance.get(`firestore/get_fb_user?fb_uid=${uid}`);
      setUserInfos(response.data);
      console.info("Fetch client infos successfully : ", response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching user infos:", error);
    }
  };

  const signUp = async (email, password, name) => {
    try {
      await createUserWithEmailAndPassword(firebaseAuth, email, password).then(
        () => {
          updateProfile(firebaseAuth.currentUser, {
            displayName: name,
          });
        }
      );
      console.info("User signed up successfully");
      return;
    } catch (err) {
      console.error(err.message);
      return err.message;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(firebaseAuth);
      setAuthToken(null);
      console.info("User signed out successfully");
    } catch (err) {
      console.error(err.message);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isSignedIn,
        user,
        userInfos,
        isLoading,
        selectedLeadId,
        signIn,
        signUp,
        signOut,
        setSelectedLeadId,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};
